import React, { useState, useEffect, useCallback, memo } from 'react';
import './App.css';
import { 
  CheckCircle, AlertCircle, Brain, Heart, TrendingUp, Calendar,
  ChevronLeft, ChevronRight, Send, Target, Users, Zap, Shield,
  Award, MapPin, Lightbulb, Compass, Globe
} from 'lucide-react';
import { useLanguage } from './LanguageContext';

// =============== API CONFIGURATION ===============
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

const API_BASE_URL = getApiUrl();

// =============== LANGUAGE SELECTOR COMPONENT ===============
const LanguageSelector = () => {
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  
  return (
    <div className="language-selector">
      <div className="language-selector-inner">
        <Globe size={18} />
        <select 
          className="language-select"
          value={currentLanguage}
          onChange={(e) => changeLanguage(e.target.value)}
        >
          {Object.entries(languages).map(([code, lang]) => (
            <option key={code} value={code}>
              {lang.nativeName}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// =============== FORM COMPONENTS ===============
const InputCard = memo(({ field, label, type = 'text', placeholder = '', icon = null, min = null, max = null, value, onChange, error }) => {
  return (
    <div className="form-group">
      <label className="form-label">
        {icon && <span className="label-icon">{icon}</span>}
        {label}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
        min={min}
        max={max}
        className={`form-input ${error ? 'error' : ''}`}
      />
      {error && (
        <div className="error-message">
          <AlertCircle size={12} />
          {error}
        </div>
      )}
    </div>
  );
});

const SelectCard = memo(({ field, label, options, icon = null, showOther = false, otherField = null, value, otherValue, onChange, onDurationChange, t }) => {
  return (
    <div className="form-group">
      <label className="form-label">
        {icon && <span className="label-icon">{icon}</span>}
        {label}
      </label>
      <select
        value={value || ''}
        onChange={(e) => {
          if (field === 'assessmentDuration' && onDurationChange) {
            onDurationChange(e.target.value);
          } else {
            onChange(field, e.target.value);
          }
        }}
        className="form-select"
      >
        <option value="">{t ? t('forms.placeholders.selectOption', { label }) : `Select ${label}`}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
        {showOther && <option value="other">{t ? t('forms.options.genders.other') : 'Other (please specify)'}</option>}
      </select>
      
      {showOther && value === 'other' && otherField && (
        <input
          type="text"
          value={otherValue || ''}
          onChange={(e) => onChange(otherField, e.target.value)}
          placeholder={t ? t('forms.placeholders.otherSpecify') : 'Please specify...'}
          className="form-input"
        />
      )}
    </div>
  );
});

const TextAreaCard = memo(({ field, label, placeholder, icon = null, value, onChange }) => {
  return (
    <div className="form-group">
      <label className="form-label">
        {icon && <span className="label-icon">{icon}</span>}
        {label}
      </label>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="form-textarea"
      />
    </div>
  );
});

const SliderCard = memo(({ field, label, min = 1, max = 10, icon = null, value, onChange, t }) => {
  return (
    <div className="form-group">
      <label className="form-label">
        {icon && <span className="label-icon">{icon}</span>}
        {label}
      </label>
      <div className="slider-container">
        <div className="slider-track">
          <input
            type="range"
            min={min}
            max={max}
            value={value || 5}
            onChange={(e) => onChange(field, parseInt(e.target.value))}
            className="slider"
          />
          <div className="slider-value">{value || 5}</div>
        </div>
        <div className="slider-labels">
          <span>{t ? t('messages.low') : 'Low'} ({min})</span>
          <span>{t ? t('messages.high') : 'High'} ({max})</span>
        </div>
      </div>
    </div>
  );
});

const EmotionSelector = memo(({ field, label, options, categories = null, icon = null, showOther = false, otherField = null, value, otherValue, onMultiSelect, onChange, t }) => {
  return (
    <div className="multi-select-container">
      <h3 className="category-title">
        {icon && <span>{icon}</span>}
        {label}
      </h3>
      
      {categories ? (
        Object.keys(categories).map(category => (
          <div key={category} className="emotion-category">
            <h4 className="category-title">
              {category === 'positive' && '😊 Positive Emotions'}
              {category === 'neutral' && '😐 Neutral States'}
              {category === 'challenging' && '😔 Challenging Emotions'}
              {category === 'physical' && '🔥 Physical & Sensual Emotions'}
              {category === 'complex' && '🌀 Complex Mixed Emotions'}
            </h4>
            <div className="multi-select-grid">
              {categories[category].map(option => (
                <button
                  key={option.value}
                  onClick={() => onMultiSelect(field, option.value)}
                  className={`multi-select-btn ${value.includes(option.value) ? 'multi-select-btn-selected' : ''}`}
                  data-category={category}
                >
                  <div>{option.label}</div>
                  <div className="emotion-explanation">{option.explanation}</div>
                </button>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="multi-select-grid">
          {options.map(option => (
            <button
              key={option.value}
              onClick={() => onMultiSelect(field, option.value)}
              className={`multi-select-btn ${value.includes(option.value) ? 'multi-select-btn-selected' : ''}`}
              data-category={option.category || 'default'}
            >
              <div>{option.label}</div>
              {option.explanation && <div className="emotion-explanation">{option.explanation}</div>}
            </button>
          ))}
        </div>
      )}
      
      {showOther && (
        <input
          type="text"
          value={otherValue || ''}
          onChange={(e) => onChange(otherField, e.target.value)}
          placeholder={t ? t('forms.placeholders.otherNotListed') : 'Other options not listed above...'}
          className="form-input"
        />
      )}
      
      {value.length > 0 && (
        <div className="selection-summary">
          <Award size={16} />
          <span>{t ? t('messages.selected') : 'Selected'}: {value.length} {value.length !== 1 ? (t ? t('messages.itemsPlural') : 'items') : (t ? t('messages.items') : 'item')}</span>
        </div>
      )}
    </div>
  );
});

const EmotionalIntelligenceAssessmentPlatform = () => {
  const { t, currentLanguage } = useLanguage();
  
  // Main state
  const [assessmentType, setAssessmentType] = useState('progress');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Progress Tracker Form Data
  const [progressData, setProgressData] = useState({
    email: '',
    assessmentDuration: 'week',
    assessmentStartDate: '',
    assessmentEndDate: '',
    age: '',
    gender: '',
    genderOther: '',
    location: '',
    incidentCount: '',
    overallStressLevel: 5,
    majorLifeEvents: '',
    frequentEmotions: [],
    otherFrequentEmotions: '',
    challengingSituations: [],
    otherChallengingSituation: '',
    timePatterns: [],
    otherTimePatterns: '',
    environmentalFactors: [],
    otherEnvironmentalFactors: '',
    selfAwareness: 5,
    selfRegulation: 5,
    emotionalBalance: 5,
    empathy: 5,
    socialSkills: 5,
    copingStrategies: [],
    otherCopingStrategies: '',
    copingEffectiveness: 5,
    newStrategiesTried: [],
    otherNewStrategies: '',
    biggestWin: '',
    biggestStruggle: '',
    mainTriggers: '',
    focusArea: '',
    satisfactionLevel: 5,
    goalsNextPeriod: '',
    situationsToImprove: '',
    supportNeeded: '',
    talkWithAdmin: false
  });

  // Incident Logger Form Data
  const [incidentData, setIncidentData] = useState({
    email: '',
    age: '',
    gender: '',
    genderOther: '',
    location: '',
    incidentLocation: '',
    otherIncidentLocation: '',
    peopleInvolved: '',
    peopleInvolvedOther: '',
    incidentSeverity: 3,
    primaryEmotion: '',
    secondaryEmotions: [],
    otherSecondaryEmotions: '',
    emotionIntensity: 5,
    emotionDuration: '',
    triggerCategory: '',
    triggerCategoryOther: '',
    situationDescription: '',
    triggerFamiliarity: '',
    physicalSensations: [],
    otherPhysicalSensations: '',
    immediateReaction: '',
    howYouHandledIt: [],
    otherHowYouHandledIt: '',
    responseEffectiveness: 5,
    whatYouWishYouDid: '',
    howYouFeel: '',
    currentIntensity: 5,
    keyInsight: '',
    wantPersonalizedReport: false,
    recurringPattern: false,
    discussWithAdmin: false,
    priorityLevel: 'medium'
  });

  // COMPREHENSIVE EMOTIONS - Greatly Expanded
  const allEmotions = {
    positive: [
      { value: 'joyful', label: '😊 Joyful', explanation: 'Deep happiness and delight from within' },
      { value: 'grateful', label: '🙏 Grateful', explanation: 'Feeling thankful and appreciative' },
      { value: 'excited', label: '🤩 Excited', explanation: 'Enthusiastic anticipation and energy' },
      { value: 'peaceful', label: '☮️ Peaceful', explanation: 'Calm and tranquil state of being' },
      { value: 'confident', label: '💪 Confident', explanation: 'Self-assured and capable feeling' },
      { value: 'loving', label: '❤️ Loving', explanation: 'Feeling deep affection and care' },
      { value: 'hopeful', label: '🌅 Hopeful', explanation: 'Optimistic about the future' },
      { value: 'proud', label: '🏆 Proud', explanation: 'Satisfaction with achievements' },
      { value: 'energized', label: '⚡ Energized', explanation: 'Full of vitality and motivation' },
      { value: 'content', label: '😌 Content', explanation: 'Satisfied and at ease' },
      { value: 'inspired', label: '✨ Inspired', explanation: 'Feeling motivated and creative' },
      { value: 'euphoric', label: '🚀 Euphoric', explanation: 'Intense happiness and elation' },
      { value: 'blissful', label: '🌸 Blissful', explanation: 'Perfect happiness and serenity' },
      { value: 'accomplished', label: '🎯 Accomplished', explanation: 'Feeling of achievement and success' },
      { value: 'appreciated', label: '🌟 Appreciated', explanation: 'Feeling valued and recognized' },
      { value: 'playful', label: '🎭 Playful', explanation: 'Light-hearted and fun-loving' },
      { value: 'serene', label: '🕊️ Serene', explanation: 'Calm and undisturbed' },
      { value: 'fulfilled', label: '💫 Fulfilled', explanation: 'Deeply satisfied and complete' },
      { value: 'ecstatic', label: '🎉 Ecstatic', explanation: 'Overwhelming joy and happiness' },
      { value: 'empowered', label: '💫 Empowered', explanation: 'Feeling strong and in control' },
      { value: 'relieved', label: '😮‍💨 Relieved', explanation: 'Free from anxiety or distress' },
      { value: 'optimistic', label: '🌈 Optimistic', explanation: 'Expecting positive outcomes' },
      { value: 'passionate', label: '🔥 Passionate', explanation: 'Intense enthusiasm and desire' },
      { value: 'amazed', label: '🤩 Amazed', explanation: 'Filled with wonder and admiration' },
      { value: 'delighted', label: '😄 Delighted', explanation: 'Great pleasure and satisfaction' },
      { value: 'triumphant', label: '🥇 Triumphant', explanation: 'Feeling victorious and successful' },
      { value: 'affectionate', label: '🥰 Affectionate', explanation: 'Warm and tender feelings' },
      { value: 'fascinated', label: '😍 Fascinated', explanation: 'Captivated and deeply interested' },
      { value: 'alive', label: '🌱 Alive', explanation: 'Vibrant and fully present' },
      { value: 'liberated', label: '🕊️ Liberated', explanation: 'Free from constraints' }
    ],
    neutral: [
      { value: 'curious', label: '🤔 Curious', explanation: 'Interested in learning more' },
      { value: 'thoughtful', label: '💭 Thoughtful', explanation: 'Contemplative and reflective' },
      { value: 'focused', label: '🎯 Focused', explanation: 'Concentrated and attentive' },
      { value: 'calm', label: '😐 Calm', explanation: 'Emotionally stable and composed' },
      { value: 'surprised', label: '😮 Surprised', explanation: 'Unexpected but neutral reaction' },
      { value: 'nostalgic', label: '📸 Nostalgic', explanation: 'Wistful longing for the past' },
      { value: 'contemplative', label: '🧐 Contemplative', explanation: 'Deep in thought' },
      { value: 'alert', label: '👀 Alert', explanation: 'Aware and attentive' },
      { value: 'neutral', label: '😶 Neutral', explanation: 'Neither positive nor negative' },
      { value: 'detached', label: '🎭 Detached', explanation: 'Emotionally distant or removed' },
      { value: 'observant', label: '🔍 Observant', explanation: 'Watching and noticing carefully' },
      { value: 'pensive', label: '🤷 Pensive', explanation: 'Engaged in serious thought' },
      { value: 'indifferent', label: '😑 Indifferent', explanation: 'Lacking interest or concern' },
      { value: 'accepting', label: '🙏 Accepting', explanation: 'Open to what is happening' },
      { value: 'perplexed', label: '🤨 Perplexed', explanation: 'Puzzled and uncertain' },
      { value: 'intrigued', label: '🤔 Intrigued', explanation: 'Fascinated and wanting to know more' },
      { value: 'ambivalent', label: '😐 Ambivalent', explanation: 'Having mixed feelings' },
      { value: 'composed', label: '😌 Composed', explanation: 'Collected and self-controlled' },
      { value: 'reserved', label: '🤐 Reserved', explanation: 'Holding back emotions' },
      { value: 'stoic', label: '😑 Stoic', explanation: 'Showing little emotion' },
      { value: 'meditative', label: '🧘 Meditative', explanation: 'In a reflective state' },
      { value: 'patient', label: '⏳ Patient', explanation: 'Willing to wait calmly' }
    ],
    challenging: [
      { value: 'anxious', label: '😰 Anxious', explanation: 'Worried about future events' },
      { value: 'frustrated', label: '😤 Frustrated', explanation: 'Blocked from achieving goals' },
      { value: 'sad', label: '😢 Sad', explanation: 'Feeling sorrow or grief' },
      { value: 'angry', label: '😠 Angry', explanation: 'Strong displeasure or hostility' },
      { value: 'overwhelmed', label: '😵 Overwhelmed', explanation: 'Feeling unable to cope' },
      { value: 'disappointed', label: '😞 Disappointed', explanation: 'Unmet expectations' },
      { value: 'lonely', label: '😔 Lonely', explanation: 'Feeling isolated or disconnected' },
      { value: 'ashamed', label: '😳 Ashamed', explanation: 'Embarrassed about behavior' },
      { value: 'fearful', label: '😱 Fearful', explanation: 'Afraid of potential threat' },
      { value: 'confused', label: '😕 Confused', explanation: 'Uncertain about situation' },
      { value: 'stressed', label: '😬 Stressed', explanation: 'Under pressure and tension' },
      { value: 'irritated', label: '😒 Irritated', explanation: 'Mildly annoyed or bothered' },
      { value: 'depressed', label: '😞 Depressed', explanation: 'Deep sadness and hopelessness' },
      { value: 'jealous', label: '😒 Jealous', explanation: 'Envious of others' },
      { value: 'guilty', label: '😔 Guilty', explanation: 'Feeling responsible for wrongdoing' },
      { value: 'resentful', label: '😤 Resentful', explanation: 'Bitter about perceived injustice' },
      { value: 'embarrassed', label: '😳 Embarrassed', explanation: 'Self-conscious and awkward' },
      { value: 'betrayed', label: '💔 Betrayed', explanation: 'Feeling deceived or let down' },
      { value: 'hopeless', label: '😩 Hopeless', explanation: 'Without hope or optimism' },
      { value: 'disgusted', label: '🤢 Disgusted', explanation: 'Feeling revulsion or distaste' },
      { value: 'rejected', label: '😔 Rejected', explanation: 'Feeling unwanted or dismissed' },
      { value: 'vulnerable', label: '🥺 Vulnerable', explanation: 'Exposed and defenseless' },
      { value: 'lustful', label: '🔥 Lustful', explanation: 'Intense physical or sexual desire' },
      { value: 'envious', label: '😒 Envious', explanation: 'Wanting what others have' },
      { value: 'humiliated', label: '😣 Humiliated', explanation: 'Deeply embarrassed and degraded' },
      { value: 'devastated', label: '💔 Devastated', explanation: 'Completely overwhelmed by loss' },
      { value: 'tormented', label: '😖 Tormented', explanation: 'Severe mental or physical suffering' },
      { value: 'bitter', label: '😤 Bitter', explanation: 'Resentful and angry about treatment' },
      { value: 'vengeful', label: '😡 Vengeful', explanation: 'Seeking revenge or retaliation' },
      { value: 'paranoid', label: '👁️ Paranoid', explanation: 'Suspicious and distrustful' },
      { value: 'insecure', label: '😟 Insecure', explanation: 'Lacking confidence in oneself' },
      { value: 'helpless', label: '😔 Helpless', explanation: 'Unable to help oneself' },
      { value: 'panicked', label: '😱 Panicked', explanation: 'Sudden overwhelming fear' },
      { value: 'mortified', label: '😱 Mortified', explanation: 'Extremely embarrassed' },
      { value: 'terrified', label: '😨 Terrified', explanation: 'Extremely frightened' },
      { value: 'outraged', label: '😡 Outraged', explanation: 'Extremely angry and shocked' },
      { value: 'miserable', label: '😩 Miserable', explanation: 'Very unhappy and uncomfortable' },
      { value: 'desperate', label: '😰 Desperate', explanation: 'Feeling hopeless and willing to do anything' },
      { value: 'apathetic', label: '😶 Apathetic', explanation: 'Lack of interest or enthusiasm' },
      { value: 'numb', label: '😶‍🌫️ Numb', explanation: 'Feeling emotionally disconnected' },
      { value: 'restless', label: '😣 Restless', explanation: 'Unable to rest or be still' },
      { value: 'agitated', label: '😤 Agitated', explanation: 'Feeling troubled or disturbed' }
    ],
    physical: [
      { value: 'aroused', label: '🔥 Aroused', explanation: 'Sexually or physically excited' },
      { value: 'sensual', label: '💋 Sensual', explanation: 'Relating to physical senses and pleasure' },
      { value: 'desire', label: '💕 Desire', explanation: 'Strong feeling of wanting someone' },
      { value: 'attraction', label: '😍 Attraction', explanation: 'Physical or emotional pull toward someone' },
      { value: 'intimate', label: '💞 Intimate', explanation: 'Feeling close and connected' },
      { value: 'seductive', label: '😏 Seductive', explanation: 'Tempting and alluring' },
      { value: 'yearning', label: '💔 Yearning', explanation: 'Deep longing for someone' },
      { value: 'craving', label: '🤤 Craving', explanation: 'Intense desire or need' }
    ],
    complex: [
      { value: 'bittersweet', label: '🍯 Bittersweet', explanation: 'Pleasant but tinged with sadness' },
      { value: 'conflicted', label: '⚖️ Conflicted', explanation: 'Having contradictory feelings' },
      { value: 'wistful', label: '🌅 Wistful', explanation: 'Gentle sadness mixed with longing' },
      { value: 'melancholic', label: '🌧️ Melancholic', explanation: 'Thoughtful sadness' },
      { value: 'euphoric-crash', label: '🎢 Euphoric Crash', explanation: 'High followed by sudden low' },
      { value: 'love-hate', label: '💔❤️ Love-Hate', explanation: 'Simultaneous love and hate' },
      { value: 'anticipatory-anxiety', label: '⏰😰 Anticipatory Anxiety', explanation: 'Anxiety about future events' },
      { value: 'guilty-pleasure', label: '😈😊 Guilty Pleasure', explanation: 'Enjoyment mixed with guilt' }
    ]
  };

  // Enhanced coping strategies with categories (including both healthy and unhealthy)
  const copingStrategies = [
    // Healthy Mindfulness & Meditation
    { value: 'deep-breathing', label: '🫁 Deep Breathing Exercises', category: 'mindfulness' },
    { value: 'guided-meditation', label: '🧘‍♀️ Guided Meditation', category: 'mindfulness' },
    { value: 'mindfulness-meditation', label: '🎯 Mindfulness Meditation', category: 'mindfulness' },
    { value: 'body-scan', label: '🔍 Body Scan Meditation', category: 'mindfulness' },
    { value: 'progressive-relaxation', label: '🌊 Progressive Muscle Relaxation', category: 'mindfulness' },
    { value: 'visualization', label: '🌈 Visualization Techniques', category: 'mindfulness' },
    
    // Healthy Physical Activities
    { value: 'cardio-exercise', label: '🏃‍♂️ Cardio Exercise', category: 'physical' },
    { value: 'strength-training', label: '🏋️‍♀️ Strength Training', category: 'physical' },
    { value: 'yoga-practice', label: '🧘‍♂️ Yoga Practice', category: 'physical' },
    { value: 'dancing', label: '💃 Dancing', category: 'physical' },
    { value: 'nature-walks', label: '🌲 Nature Walks', category: 'physical' },
    { value: 'swimming', label: '🏊‍♀️ Swimming', category: 'physical' },
    { value: 'sports', label: '⚽ Playing Sports', category: 'physical' },
    { value: 'stretching', label: '🤸‍♀️ Stretching', category: 'physical' },
    
    // Healthy Cognitive Strategies
    { value: 'journaling-thoughts', label: '📝 Thought Journaling', category: 'cognitive' },
    { value: 'gratitude-journaling', label: '🙏 Gratitude Journaling', category: 'cognitive' },
    { value: 'cognitive-reframing', label: '🔄 Cognitive Reframing', category: 'cognitive' },
    { value: 'positive-self-talk', label: '💪 Positive Self-Talk', category: 'cognitive' },
    { value: 'problem-solving', label: '🧩 Problem-Solving Techniques', category: 'cognitive' },
    { value: 'goal-setting', label: '🎯 Goal Setting', category: 'cognitive' },
    { value: 'affirmations', label: '✨ Positive Affirmations', category: 'cognitive' },
    
    // Healthy Social Support
    { value: 'talking-friends-family', label: '👥 Talking to Friends/Family', category: 'social' },
    { value: 'professional-therapy', label: '💭 Professional Therapy', category: 'social' },
    { value: 'support-groups', label: '🤝 Support Groups', category: 'social' },
    { value: 'peer-counseling', label: '👫 Peer Counseling', category: 'social' },
    { value: 'mentoring', label: '👨‍🏫 Mentoring/Coaching', category: 'social' },
    
    // Healthy Creative & Expressive
    { value: 'art-therapy', label: '🎨 Art/Creative Expression', category: 'creative' },
    { value: 'music-therapy', label: '🎵 Music Therapy', category: 'creative' },
    { value: 'writing-poetry', label: '✍️ Creative Writing/Poetry', category: 'creative' },
    { value: 'singing', label: '🎤 Singing', category: 'creative' },
    
    // Healthy Lifestyle
    { value: 'time-management', label: '⏰ Time Management', category: 'lifestyle' },
    { value: 'healthy-eating', label: '🥗 Healthy Eating', category: 'lifestyle' },
    { value: 'adequate-sleep', label: '😴 Adequate Sleep', category: 'lifestyle' },
    { value: 'limiting-caffeine', label: '☕ Limiting Caffeine/Alcohol', category: 'lifestyle' },
    { value: 'spiritual-practices', label: '🙏 Spiritual Practices', category: 'spiritual' },
    { value: 'prayer', label: '🤲 Prayer', category: 'spiritual' },
    { value: 'reading-books', label: '📚 Reading Self-Help Books', category: 'educational' },
    
    // Unhealthy/Problematic Coping Strategies
    { value: 'overeating', label: '🍔 Overeating/Binge Eating', category: 'unhealthy' },
    { value: 'undereating', label: '🚫🍽️ Undereating/Skipping Meals', category: 'unhealthy' },
    { value: 'emotional-eating', label: '😭🍫 Emotional Eating (sweets, junk food)', category: 'unhealthy' },
    { value: 'excessive-alcohol', label: '🍷 Drinking Alcohol Excessively', category: 'unhealthy' },
    { value: 'substance-use', label: '💊 Using Drugs/Substances', category: 'unhealthy' },
    { value: 'smoking', label: '🚬 Smoking/Vaping', category: 'unhealthy' },
    { value: 'social-media-scrolling', label: '📱 Endless Social Media Scrolling', category: 'unhealthy' },
    { value: 'binge-watching', label: '📺 Binge Watching TV/Netflix', category: 'unhealthy' },
    { value: 'gaming-excess', label: '🎮 Excessive Gaming', category: 'unhealthy' },
    { value: 'online-shopping', label: '🛒 Impulse Online Shopping', category: 'unhealthy' },
    { value: 'sexual-behavior', label: '🔞 Compulsive Sexual Behavior', category: 'unhealthy' },
    { value: 'self-harm', label: '💔 Self-Harm/Cutting', category: 'unhealthy' },
    { value: 'isolation', label: '🚪 Isolating from Others', category: 'unhealthy' },
    { value: 'aggression', label: '😡 Verbal/Physical Aggression', category: 'unhealthy' },
    { value: 'bottling-up', label: '🤐 Bottling Up Emotions', category: 'unhealthy' },
    { value: 'denial', label: '🙈 Denial/Pretending Nothing Happened', category: 'unhealthy' },
    { value: 'blame-others', label: '👉 Blaming Others', category: 'unhealthy' },
    { value: 'rumination', label: '🔄 Ruminating/Obsessive Thinking', category: 'unhealthy' },
    { value: 'catastrophizing', label: '💥 Catastrophizing/Worst-Case Thinking', category: 'unhealthy' },
    { value: 'revenge-planning', label: '⚔️ Planning Revenge/Retaliation', category: 'unhealthy' },
    { value: 'perfectionism', label: '💯 Perfectionist Overcompensation', category: 'unhealthy' },
    { value: 'workaholism', label: '💼 Working Excessively to Avoid Feelings', category: 'unhealthy' },
    { value: 'reckless-behavior', label: '🏎️ Reckless/Dangerous Behavior', category: 'unhealthy' },
    { value: 'gossiping', label: '🗣️ Gossiping/Talking Behind People\'s Backs', category: 'unhealthy' },
    { value: 'passive-aggression', label: '😤 Passive Aggressive Behavior', category: 'unhealthy' },
    
    // Neutral/Mixed Strategies
    { value: 'sleeping-excess', label: '😴 Sleeping Excessively', category: 'mixed' },
    { value: 'cleaning-organizing', label: '🧹 Excessive Cleaning/Organizing', category: 'mixed' },
    { value: 'distraction', label: '🎯 Pure Distraction (avoiding the issue)', category: 'mixed' },
    { value: 'crying', label: '😭 Crying it Out', category: 'mixed' },
    { value: 'venting-repeatedly', label: '🗣️ Venting Repeatedly to Same People', category: 'mixed' }
  ];

  // Best practice new coping strategies to suggest
  const newCopingStrategiesOptions = [
    // Mindfulness & Regulation
    { value: 'box-breathing', label: '📦 Box Breathing (4-4-4-4 technique)', explanation: 'Inhale for 4, hold for 4, exhale for 4, hold for 4. Repeat to calm nervous system.' },
    { value: '5-4-3-2-1-grounding', label: '🔢 5-4-3-2-1 Grounding Technique', explanation: 'Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, 1 you taste.' },
    { value: 'emotion-labeling', label: '🏷️ Emotion Labeling & Naming', explanation: 'Precisely identify and name your emotions to reduce their intensity.' },
    { value: 'tipp-technique', label: '🧊 TIPP Technique (DBT)', explanation: 'Temperature, Intense exercise, Paced breathing, Progressive muscle relaxation for crisis moments.' },
    { value: 'wise-mind', label: '🧠 Wise Mind Practice', explanation: 'Balance emotional mind and rational mind to make decisions from wisdom.' },
    
    // Cognitive Techniques
    { value: 'thought-records', label: '📋 Thought Record Worksheets', explanation: 'Systematically examine thoughts, emotions, and evidence for/against beliefs.' },
    { value: 'reappraisal-technique', label: '🔄 Cognitive Reappraisal', explanation: 'Reframe situations by finding alternative, more balanced perspectives.' },
    { value: 'defusion-techniques', label: '🎭 Cognitive Defusion', explanation: 'Create distance from thoughts by seeing them as mental events, not facts.' },
    { value: 'self-compassion', label: '💕 Self-Compassion Practice', explanation: 'Treat yourself with the same kindness you\'d show a good friend.' },
    { value: 'values-clarification', label: '🎯 Values Clarification & Living', explanation: 'Identify your core values and align actions with what matters most.' },
    
    // Behavioral Techniques
    { value: 'behavioral-activation', label: '🎬 Behavioral Activation', explanation: 'Schedule pleasant and meaningful activities even when you don\'t feel like it.' },
    { value: 'opposite-action', label: '↔️ Opposite Action (DBT)', explanation: 'Do the opposite of what your emotion urges when the emotion isn\'t helpful.' },
    { value: 'gradual-exposure', label: '👣 Gradual Exposure Therapy', explanation: 'Gradually face fears in a controlled way to reduce anxiety over time.' },
    { value: 'radical-acceptance', label: '🤲 Radical Acceptance', explanation: 'Fully accept reality without judging it as good or bad.' },
    { value: 'mastery-activities', label: '🏆 Mastery & Achievement Activities', explanation: 'Engage in activities that give you a sense of accomplishment.' },
    
    // Interpersonal & Social
    { value: 'assertiveness-training', label: '💪 Assertiveness Skills', explanation: 'Learn to express needs and boundaries clearly and respectfully.' },
    { value: 'dear-man', label: '📝 DEAR MAN Communication', explanation: 'Describe, Express, Assert, Reinforce, Mindful, Appear confident, Negotiate.' },
    { value: 'boundary-setting', label: '🚧 Healthy Boundary Setting', explanation: 'Learn to say no and protect your emotional and physical space.' },
    { value: 'empathy-building', label: '💗 Empathy & Perspective-Taking', explanation: 'Practice understanding others\' viewpoints to improve relationships.' },
    { value: 'conflict-resolution', label: '🤝 Conflict Resolution Skills', explanation: 'Learn healthy ways to address and resolve interpersonal conflicts.' },
    
    // Somatic & Body-Based
    { value: 'progressive-muscle', label: '💪 Progressive Muscle Relaxation', explanation: 'Systematically tense and release muscle groups to reduce physical tension.' },
    { value: 'somatic-experiencing', label: '🌊 Somatic Experiencing', explanation: 'Release trauma and stress stored in the body through gentle body awareness.' },
    { value: 'cold-water-therapy', label: '🧊 Cold Water Face Plunge', explanation: 'Splash cold water on face or hold ice to quickly activate parasympathetic nervous system.' },
    { value: 'bilateral-stimulation', label: '🤲 Bilateral Stimulation', explanation: 'Cross-lateral movements or butterfly hug to calm emotional overwhelm.' },
    { value: 'yoga-trauma-informed', label: '🧘 Trauma-Informed Yoga', explanation: 'Gentle yoga focused on body awareness and nervous system regulation.' },
    
    // Creative & Expressive
    { value: 'art-therapy-trauma', label: '🎨 Art Therapy for Emotional Expression', explanation: 'Use creative expression to process emotions when words aren\'t enough.' },
    { value: 'music-emotion-regulation', label: '🎵 Music for Emotion Regulation', explanation: 'Use specific music to shift emotional states and process feelings.' },
    { value: 'narrative-therapy', label: '📖 Narrative Therapy/Story Writing', explanation: 'Rewrite your story and separate yourself from problems.' },
    { value: 'dance-movement', label: '💃 Dance/Movement Therapy', explanation: 'Use body movement to express and process emotions.' },
    
    // Technology & Modern Approaches
    { value: 'emotion-tracking-apps', label: '📱 Emotion Tracking Apps', explanation: 'Use apps to monitor patterns in emotions, triggers, and coping effectiveness.' },
    { value: 'biofeedback', label: '📊 Biofeedback Training', explanation: 'Learn to control physiological responses using real-time feedback.' },
    { value: 'vr-relaxation', label: '🥽 VR Relaxation/Meditation', explanation: 'Use virtual reality for immersive relaxation and mindfulness experiences.' },
    { value: 'online-therapy', label: '💻 Online Therapy Platforms', explanation: 'Access professional support through secure online therapy platforms.' }
  ];

  // Enhanced triggers with more comprehensive options
  const triggers = [
    { value: 'work-stress', label: '💼 Work Pressure/Deadlines' },
    { value: 'relationship-conflict', label: '💔 Relationship Conflicts' },
    { value: 'financial-worry', label: '💰 Financial Stress' },
    { value: 'health-concerns', label: '🏥 Health Worries' },
    { value: 'family-dynamics', label: '👨‍👩‍👧‍👦 Family Issues' },
    { value: 'social-situations', label: '👥 Social Pressure' },
    { value: 'time-pressure', label: '⏰ Time Constraints' },
    { value: 'perfectionism', label: '💯 High Standards/Perfectionism' },
    { value: 'uncertainty', label: '❓ Unknown Outcomes' },
    { value: 'criticism', label: '📝 Criticism/Feedback' },
    { value: 'change-transition', label: '🔄 Life Changes/Transitions' },
    { value: 'loss-grief', label: '💔 Loss/Grief' },
    { value: 'academic-pressure', label: '📚 Academic Pressure' },
    { value: 'technology-issues', label: '📱 Technology Problems' },
    { value: 'traffic-commute', label: '🚗 Traffic/Commuting' },
    { value: 'sleep-deprivation', label: '😴 Sleep Deprivation' },
    { value: 'physical-pain', label: '🤕 Physical Pain/Illness' },
    { value: 'weather-environment', label: '🌦️ Weather/Environment' },
    { value: 'news-media', label: '📺 News/Media Consumption' },
    { value: 'comparison-others', label: '👥 Comparing to Others' }
  ];

  // Time patterns - more detailed and comprehensive
  const detailedTimePatterns = [
    // Specific Times of Day
    { value: 'very-early-morning', label: '🌅 Very Early Morning (4-6am)' },
    { value: 'early-morning', label: '☀️ Early Morning (6-8am)' },
    { value: 'morning-commute', label: '🚗 Morning Commute Time' },
    { value: 'mid-morning', label: '🌤️ Mid Morning (8-10am)' },
    { value: 'late-morning', label: '☀️ Late Morning (10am-12pm)' },
    { value: 'lunch-time', label: '🍽️ Lunch Time (12-2pm)' },
    { value: 'early-afternoon', label: '🌤️ Early Afternoon (2-4pm)' },
    { value: 'late-afternoon', label: '🌅 Late Afternoon (4-6pm)' },
    { value: 'evening-commute', label: '🚗 Evening Commute Time' },
    { value: 'early-evening', label: '🌆 Early Evening (6-8pm)' },
    { value: 'dinner-time', label: '🍽️ Dinner Time (7-9pm)' },
    { value: 'late-evening', label: '🌃 Late Evening (8-10pm)' },
    { value: 'bedtime-routine', label: '😴 Bedtime/Wind-down Time' },
    { value: 'night-time', label: '🌙 Night Time (10pm-12am)' },
    { value: 'late-night', label: '🌚 Late Night (12am-4am)' },
    { value: 'middle-of-night', label: '🌃 Middle of Night (2-4am)' },
    
    // Days of Week
    { value: 'monday-blues', label: '😔 Monday (start of work week)' },
    { value: 'tuesday-wednesday', label: '📅 Tuesday-Wednesday (mid-week)' },
    { value: 'thursday-stress', label: '😰 Thursday (pre-weekend stress)' },
    { value: 'friday-anxiety', label: '😬 Friday (end of week pressure)' },
    { value: 'friday-evening', label: '🎉 Friday Evening (weekend transition)' },
    { value: 'saturday-morning', label: '🌅 Saturday Morning' },
    { value: 'sunday-evening', label: '😰 Sunday Evening (Sunday Scaries)' },
    { value: 'weekdays', label: '📅 Weekdays (Mon-Fri)' },
    { value: 'weekends', label: '🎉 Weekends (Sat-Sun)' },
    
    // Special Times & Events
    { value: 'holidays', label: '🎊 Holidays/Special Occasions' },
    { value: 'anniversaries', label: '💕 Anniversaries/Significant Dates' },
    { value: 'birthdays', label: '🎂 Birthdays (yours or others)' },
    { value: 'before-events', label: '⏰ Before Important Events/Deadlines' },
    { value: 'after-events', label: '📅 After Major Events (come-down)' },
    { value: 'vacation-time', label: '🏖️ During Vacations/Time Off' },
    { value: 'exam-periods', label: '📚 Exam/Test Periods' },
    { value: 'performance-reviews', label: '💼 Performance Review Times' },
    
    // Seasonal & Weather Patterns
    { value: 'winter-months', label: '❄️ Winter Months (SAD patterns)' },
    { value: 'spring-season', label: '🌸 Spring Season' },
    { value: 'summer-heat', label: '☀️ Hot Summer Days' },
    { value: 'fall-season', label: '🍂 Fall/Autumn Season' },
    { value: 'rainy-days', label: '🌧️ Rainy/Gloomy Days' },
    { value: 'sunny-days', label: '☀️ Very Sunny Days' },
    { value: 'stormy-weather', label: '⛈️ Stormy Weather' },
    { value: 'seasonal-transitions', label: '🔄 Seasonal Transitions' },
    
    // Life Rhythm Patterns
    { value: 'menstrual-cycle', label: '🌙 During Menstrual Cycle' },
    { value: 'pms-time', label: '😤 PMS/Pre-menstrual Time' },
    { value: 'ovulation-time', label: '🌸 Ovulation Time' },
    { value: 'sleep-deprived', label: '😴 When Sleep Deprived' },
    { value: 'hungry-times', label: '🍽️ When Hungry/Low Blood Sugar' },
    { value: 'caffeine-crash', label: '☕ Caffeine Crash Times' },
    { value: 'medication-times', label: '💊 Around Medication Times' },
    
    // Social & Activity Patterns
    { value: 'social-gatherings', label: '👥 During Social Gatherings' },
    { value: 'alone-time', label: '🧘 During Alone Time' },
    { value: 'family-visits', label: '👨‍👩‍👧‍👦 Family Visit Times' },
    { value: 'work-meetings', label: '💼 During Work Meetings' },
    { value: 'decision-making-times', label: '🤔 When Making Big Decisions' },
    { value: 'conflict-times', label: '⚔️ During/After Conflicts' },
    { value: 'transition-times', label: '🔄 Transition Periods (between activities)' }
  ];

  // Environmental factors - comprehensive and detailed
  const detailedEnvironmentalFactors = [
    // Home Environments - Detailed
    { value: 'home-bedroom', label: '🛏️ Bedroom (your private space)' },
    { value: 'home-kitchen', label: '🍳 Kitchen (cooking/eating area)' },
    { value: 'home-living-room', label: '🛋️ Living Room (family gathering space)' },
    { value: 'home-bathroom', label: '🚿 Bathroom (personal care space)' },
    { value: 'home-office', label: '💻 Home Office/Study Room' },
    { value: 'home-garden', label: '🌱 Garden/Backyard/Outdoor Space' },
    { value: 'home-basement', label: '🏠 Basement/Lower Level' },
    { value: 'home-garage', label: '🚗 Garage/Storage Areas' },
    { value: 'shared-living-space', label: '🏠 Shared Living Space (roommates)' },
    { value: 'childhood-home', label: '🏠 Childhood Home/Parents\' House' },
    
    // Work Environments - Detailed
    { value: 'work-office-cubicle', label: '💼 Office Cubicle/Desk Area' },
    { value: 'work-private-office', label: '🚪 Private Office' },
    { value: 'work-open-office', label: '👥 Open Office Environment' },
    { value: 'work-meeting-rooms', label: '📊 Meeting/Conference Rooms' },
    { value: 'work-cafeteria', label: '🍽️ Work Cafeteria/Break Room' },
    { value: 'work-parking-lot', label: '🚗 Work Parking Lot' },
    { value: 'work-factory-floor', label: '🏭 Factory/Manufacturing Floor' },
    { value: 'work-retail-store', label: '🛒 Retail Store/Customer Service Area' },
    { value: 'work-remote-home', label: '🏠 Remote Work from Home' },
    { value: 'work-coworking-space', label: '☕ Co-working Space' },
    { value: 'work-outdoors', label: '🌳 Outdoor Work Environment' },
    
    // Transportation
    { value: 'car-driving', label: '🚗 In the Car (while driving)' },
    { value: 'car-passenger', label: '🚙 In the Car (as passenger)' },
    { value: 'public-transport-bus', label: '🚌 Bus/Public Transportation' },
    { value: 'public-transport-train', label: '🚂 Train/Subway' },
    { value: 'airplane', label: '✈️ Airplane/Airport' },
    { value: 'walking-commute', label: '🚶 Walking/Commuting on Foot' },
    { value: 'bike-cycling', label: '🚴 Cycling/Bike Riding' },
    { value: 'traffic-jams', label: '🚦 Stuck in Traffic' },
    
    // Social & Public Spaces
    { value: 'restaurants-casual', label: '🍽️ Casual Restaurants' },
    { value: 'restaurants-fancy', label: '🥂 Fine Dining Restaurants' },
    { value: 'bars-nightlife', label: '🍺 Bars/Nightlife Venues' },
    { value: 'coffee-shops', label: '☕ Coffee Shops/Cafes' },
    { value: 'shopping-malls', label: '🛍️ Shopping Malls' },
    { value: 'grocery-stores', label: '🛒 Grocery Stores' },
    { value: 'retail-stores', label: '👕 Retail/Clothing Stores' },
    { value: 'movie-theaters', label: '🎬 Movie Theaters' },
    { value: 'concerts-events', label: '🎵 Concerts/Live Events' },
    { value: 'social-gatherings', label: '🎉 Social Parties/Gatherings' },
    { value: 'weddings-ceremonies', label: '💒 Weddings/Ceremonies' },
    { value: 'funerals-memorials', label: '⚱️ Funerals/Memorial Services' },
    
    // Health & Wellness Spaces
    { value: 'hospitals-clinics', label: '🏥 Hospitals/Medical Clinics' },
    { value: 'dentist-office', label: '🦷 Dentist Office' },
    { value: 'therapy-counseling', label: '💭 Therapy/Counseling Office' },
    { value: 'gyms-fitness', label: '🏋️‍♀️ Gym/Fitness Centers' },
    { value: 'yoga-studios', label: '🧘 Yoga/Meditation Studios' },
    { value: 'spas-wellness', label: '💆 Spa/Wellness Centers' },
    { value: 'pharmacies', label: '💊 Pharmacies/Drug Stores' },
    
    // Educational Environments
    { value: 'schools-classrooms', label: '🎓 Schools/Classrooms' },
    { value: 'universities-campus', label: '🏫 Universities/College Campus' },
    { value: 'libraries', label: '📚 Libraries/Study Spaces' },
    { value: 'lecture-halls', label: '👨‍🏫 Lecture Halls/Auditoriums' },
    { value: 'student-dorms', label: '🏠 Student Dormitories' },
    { value: 'testing-centers', label: '📝 Testing/Exam Centers' },
    
    // Outdoor & Nature Environments
    { value: 'parks-outdoors', label: '🌳 Parks/Outdoor Recreation Areas' },
    { value: 'beaches-waterfront', label: '🏖️ Beaches/Waterfront Areas' },
    { value: 'mountains-hiking', label: '⛰️ Mountains/Hiking Trails' },
    { value: 'forests-nature', label: '🌲 Forests/Nature Reserves' },
    { value: 'urban-streets', label: '🏙️ Urban Streets/City Environment' },
    { value: 'suburban-neighborhoods', label: '🏘️ Suburban Neighborhoods' },
    { value: 'rural-countryside', label: '🌾 Rural/Countryside Areas' },
    { value: 'sports-fields', label: '⚽ Sports Fields/Athletic Venues' },
    
    // Religious & Spiritual Spaces
    { value: 'religious-places', label: '⛪ Churches/Religious Buildings' },
    { value: 'meditation-centers', label: '🧘 Meditation/Spiritual Centers' },
    { value: 'cemeteries', label: '🪦 Cemeteries/Memorial Sites' },
    
    // Entertainment & Leisure
    { value: 'gaming-arcades', label: '🎮 Gaming Arcades/Entertainment Centers' },
    { value: 'sports-stadiums', label: '🏟️ Sports Stadiums/Arenas' },
    { value: 'amusement-parks', label: '🎢 Amusement Parks/Theme Parks' },
    { value: 'museums-galleries', label: '🏛️ Museums/Art Galleries' },
    { value: 'bowling-alleys', label: '🎳 Bowling Alleys/Recreation Centers' },
    
    // Digital/Virtual Environments
    { value: 'online-virtual', label: '💻 Online/Virtual Environments' },
    { value: 'social-media', label: '📱 Social Media Platforms' },
    { value: 'video-calls', label: '📹 Video Calls/Virtual Meetings' },
    { value: 'gaming-online', label: '🎮 Online Gaming Environments' },
    { value: 'streaming-content', label: '📺 Watching Streaming Content' },
    
    // Specific Situational Environments
    { value: 'crowded-spaces', label: '👥 Crowded/Busy Spaces' },
    { value: 'quiet-spaces', label: '🤫 Very Quiet Spaces' },
    { value: 'noisy-environments', label: '🔊 Noisy/Loud Environments' },
    { value: 'small-enclosed-spaces', label: '📦 Small/Enclosed Spaces' },
    { value: 'large-open-spaces', label: '🏟️ Large Open Spaces' },
    { value: 'unfamiliar-places', label: '❓ Unfamiliar/New Places' },
    { value: 'childhood-locations', label: '👶 Childhood/Nostalgic Locations' },
    { value: 'high-stress-environments', label: '😰 High-Stress Environments' },
    { value: 'competitive-environments', label: '🏆 Competitive Environments' },
    { value: 'formal-settings', label: '👔 Formal/Professional Settings' },
    { value: 'casual-relaxed-settings', label: '😌 Casual/Relaxed Settings' }
  ];

  // Enhanced challenging situations with comprehensive options
  const challengingSituations = [
    // Work & Career
    { value: 'work-pressure', label: '💼 Work Pressure & Deadlines' },
    { value: 'job-insecurity', label: '😰 Job Insecurity/Fear of Being Fired' },
    { value: 'workplace-bullying', label: '😤 Workplace Bullying/Harassment' },
    { value: 'difficult-boss', label: '👔 Difficult Boss/Manager' },
    { value: 'toxic-work-environment', label: '☣️ Toxic Work Environment' },
    { value: 'work-life-balance', label: '⚖️ Work-Life Balance Issues' },
    { value: 'burnout', label: '🔥 Professional Burnout' },
    { value: 'imposter-syndrome-work', label: '🎭 Imposter Syndrome at Work' },
    { value: 'career-stagnation', label: '📈 Career Stagnation/Lack of Growth' },
    { value: 'workplace-discrimination', label: '⚖️ Workplace Discrimination' },
    
    // Relationships & Social
    { value: 'relationship-conflicts', label: '💔 Romantic Relationship Conflicts' },
    { value: 'breakup-divorce', label: '💔 Breakup/Divorce/Separation' },
    { value: 'family-dynamics', label: '👨‍👩‍👧‍👦 Toxic Family Dynamics' },
    { value: 'parent-child-conflict', label: '👶 Parent-Child Conflicts' },
    { value: 'friendship-drama', label: '👥 Friend Drama/Betrayal' },
    { value: 'social-rejection', label: '😔 Social Rejection/Exclusion' },
    { value: 'loneliness-isolation', label: '😔 Chronic Loneliness/Isolation' },
    { value: 'dating-struggles', label: '💕 Dating/Finding Love Struggles' },
    { value: 'infidelity', label: '💔 Infidelity/Cheating Partner' },
    { value: 'domestic-violence', label: '🚨 Domestic Violence/Abuse' },
    { value: 'sexual-assault', label: '🚨 Sexual Assault/Harassment' },
    { value: 'codependency', label: '🔗 Codependent Relationships' },
    { value: 'boundary-violations', label: '🚧 People Violating Your Boundaries' },
    
    // Financial & Material
    { value: 'financial-stress', label: '💰 Financial Stress/Money Problems' },
    { value: 'debt-bankruptcy', label: '💳 Debt/Bankruptcy' },
    { value: 'unemployment', label: '📉 Unemployment/Job Loss' },
    { value: 'housing-insecurity', label: '🏠 Housing Insecurity/Homelessness' },
    { value: 'poverty', label: '💸 Poverty/Basic Needs Not Met' },
    { value: 'financial-dependence', label: '🔗 Financial Dependence on Others' },
    
    // Health & Body
    { value: 'health-worries', label: '🏥 Health Issues/Medical Concerns' },
    { value: 'chronic-illness', label: '🩺 Chronic Illness/Pain' },
    { value: 'mental-health-crisis', label: '🧠 Mental Health Crisis' },
    { value: 'body-image', label: '🪞 Body Image/Weight Issues' },
    { value: 'eating-disorders', label: '🍽️ Eating Disorders' },
    { value: 'addiction-struggles', label: '💊 Addiction/Substance Abuse' },
    { value: 'disability-challenges', label: '♿ Disability-Related Challenges' },
    { value: 'aging-concerns', label: '👴 Aging/Getting Older' },
    
    // Life Transitions & Changes
    { value: 'major-life-changes', label: '🔄 Major Life Transitions' },
    { value: 'moving-relocation', label: '📦 Moving/Relocation' },
    { value: 'loss-grief', label: '💔 Loss/Grief/Death of Loved One' },
    { value: 'empty-nest', label: '🏠 Empty Nest Syndrome' },
    { value: 'retirement-anxiety', label: '👴 Retirement Anxiety' },
    { value: 'midlife-crisis', label: '🌗 Midlife Crisis' },
    { value: 'identity-crisis', label: '🎭 Identity Crisis/Who Am I?' },
    
    // Parenting & Children
    { value: 'parenting-challenges', label: '👶 Parenting Stress/Difficult Child' },
    { value: 'teen-rebellion', label: '🤟 Teenage Rebellion/Behavior Issues' },
    { value: 'custody-battles', label: '⚖️ Child Custody Battles' },
    { value: 'infertility', label: '👶 Infertility/Pregnancy Loss' },
    { value: 'special-needs-child', label: '🌟 Caring for Special Needs Child' },
    { value: 'child-safety-fears', label: '👶 Fear for Child\'s Safety' },
    
    // Academic & Educational
    { value: 'academic-pressure', label: '📚 Academic Pressure/School Stress' },
    { value: 'exam-anxiety', label: '📝 Exam Anxiety/Test Stress' },
    { value: 'bullying-school', label: '😤 School Bullying' },
    { value: 'college-admission', label: '🎓 College Admission Pressure' },
    { value: 'student-debt', label: '💳 Student Loan Debt' },
    { value: 'academic-failure', label: '📉 Academic Failure/Poor Grades' },
    
    // Social Issues & Discrimination
    { value: 'racism-discrimination', label: '⚖️ Racism/Racial Discrimination' },
    { value: 'gender-discrimination', label: '⚥ Gender Discrimination' },
    { value: 'lgbtq-discrimination', label: '🏳️‍🌈 LGBTQ+ Discrimination' },
    { value: 'religious-persecution', label: '⛪ Religious Persecution' },
    { value: 'cultural-conflicts', label: '🌍 Cultural Identity Conflicts' },
    
    // Technology & Modern Life
    { value: 'technology-overwhelm', label: '📱 Technology Overwhelm' },
    { value: 'social-media-pressure', label: '📱 Social Media Pressure/FOMO' },
    { value: 'cyberbullying', label: '💻 Cyberbullying/Online Harassment' },
    { value: 'privacy-concerns', label: '🔒 Privacy/Security Concerns' },
    { value: 'information-overload', label: '📰 Information Overload/News Anxiety' },
    
    // Personal Growth & Self-Worth
    { value: 'perfectionism', label: '💯 Perfectionist Tendencies' },
    { value: 'comparison-others', label: '👥 Comparing Self to Others' },
    { value: 'imposter-syndrome', label: '🎭 Imposter Syndrome' },
    { value: 'low-self-esteem', label: '😔 Low Self-Esteem/Self-Worth' },
    { value: 'decision-making', label: '🤔 Difficult Decision Making' },
    { value: 'time-management', label: '⏰ Time Management Issues' },
    { value: 'procrastination', label: '⏳ Chronic Procrastination' },
    { value: 'people-pleasing', label: '😊 People Pleasing/Can\'t Say No' },
    
    // Environmental & External Stressors
    { value: 'natural-disasters', label: '🌪️ Natural Disasters/Climate Events' },
    { value: 'political-climate', label: '🗳️ Political Climate/Social Unrest' },
    { value: 'economic-uncertainty', label: '📈 Economic Uncertainty' },
    { value: 'community-violence', label: '🚨 Community Violence/Crime' },
    { value: 'pandemic-stress', label: '😷 Pandemic/Health Crisis Stress' },
    
    // Spiritual & Existential
    { value: 'spiritual-crisis', label: '🙏 Spiritual Crisis/Loss of Faith' },
    { value: 'existential-anxiety', label: '🌌 Existential Anxiety/Meaning of Life' },
    { value: 'moral-dilemmas', label: '⚖️ Moral/Ethical Dilemmas' },
    { value: 'purpose-meaning', label: '🎯 Lack of Purpose/Meaning' }
  ];

  // Define steps with translation support
  const getProgressSteps = () => [
    { title: t('progressSteps.beforeBegin.title'), icon: Compass, description: t('progressSteps.beforeBegin.description') },
    { title: t('progressSteps.emotionalPatterns.title'), icon: TrendingUp, description: t('progressSteps.emotionalPatterns.description') },
    { title: t('progressSteps.eqSkills.title'), icon: Brain, description: t('progressSteps.eqSkills.description') },
    { title: t('progressSteps.copingRegulation.title'), icon: Shield, description: t('progressSteps.copingRegulation.description') },
    { title: t('progressSteps.socialIntelligence.title'), icon: Users, description: t('progressSteps.socialIntelligence.description') },
    { title: t('progressSteps.growthIntegration.title'), icon: Target, description: t('progressSteps.growthIntegration.description') }
  ];

  const getIncidentSteps = () => [
    { title: t('incidentSteps.groundingContext.title'), icon: MapPin, description: t('incidentSteps.groundingContext.description') },
    { title: t('incidentSteps.emotionalMapping.title'), icon: Heart, description: t('incidentSteps.emotionalMapping.description') },
    { title: t('incidentSteps.triggerAnalysis.title'), icon: Zap, description: t('incidentSteps.triggerAnalysis.description') },
    { title: t('incidentSteps.responseAnalysis.title'), icon: TrendingUp, description: t('incidentSteps.responseAnalysis.description') },
    { title: t('incidentSteps.copingEffectiveness.title'), icon: Shield, description: t('incidentSteps.copingEffectiveness.description') },
    { title: t('incidentSteps.learningIntegration.title'), icon: Target, description: t('incidentSteps.learningIntegration.description') }
  ];

  const getCurrentSteps = () => assessmentType === 'progress' ? getProgressSteps() : getIncidentSteps();

  // Add viewport meta tag programmatically if not present
  useEffect(() => {
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    
    // Ensure body and html are properly styled for 100% scale
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.width = '100%';
    document.body.style.overflowX = 'hidden';
    document.body.style.zoom = '1';
    document.body.style.transform = 'scale(1)';
    document.documentElement.style.width = '100%';
    document.documentElement.style.overflowX = 'hidden';
    document.documentElement.style.zoom = '1';
    document.documentElement.style.fontSize = '16px';
    
    // Reset any browser zoom
    if (document.body.style.zoom !== '1') {
      document.body.style.zoom = '1';
    }
  }, []);

  // Utility functions
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getDateRangeForDuration = useCallback((duration) => {
    const today = new Date();
    const endDate = new Date(today); // Always use today as end date
    let startDate;
    
    switch (duration) {
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        break;
      case '3month':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 90);
        break;
      case 'year':
        startDate = new Date(today);
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      default:
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
    }
    
    return {
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate)
    };
  }, []);

  const handleDurationChange = useCallback((duration) => {
    console.log('handleDurationChange called with:', duration);
    
    // Clear any date-related errors first
    setErrors(prev => ({
      ...prev,
      assessmentStartDate: null,
      assessmentEndDate: null
    }));

    if (duration !== 'custom') {
      console.log('Setting preset duration:', duration);
      const { startDate, endDate } = getDateRangeForDuration(duration);
      setProgressData(prev => {
        const newData = {
          ...prev,
          assessmentDuration: duration,
          assessmentStartDate: startDate,
          assessmentEndDate: endDate
        };
        console.log('Updated progressData (preset):', newData);
        return newData;
      });
    } else {
      console.log('Setting custom duration');
      // For custom, clear the dates so user can select their own
      setProgressData(prev => {
        const newData = {
          ...prev,
          assessmentDuration: 'custom',
          assessmentStartDate: '',
          assessmentEndDate: ''
        };
        console.log('Updated progressData (custom):', newData);
        return newData;
      });
    }
  }, [getDateRangeForDuration]);

  const validateDate = (dateString) => {
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return selectedDate <= today;
  };

  const getMaxDate = () => {
    return formatDateForInput(new Date());
  };

  useEffect(() => {
    if (assessmentType === 'progress' && !progressData.assessmentStartDate && progressData.assessmentDuration !== 'custom') {
      const { startDate, endDate } = getDateRangeForDuration('week');
      setProgressData(prev => ({
        ...prev,
        assessmentDuration: 'week',
        assessmentStartDate: startDate,
        assessmentEndDate: endDate
      }));
    }
  }, [assessmentType, getDateRangeForDuration, progressData.assessmentStartDate, progressData.assessmentDuration]);

  // Stable callback functions
  const handleInputChange = useCallback((field, value) => {
    if (assessmentType === 'progress') {
      setProgressData(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      setIncidentData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error for this field
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
  }, [assessmentType]);

  const handleMultiSelect = useCallback((field, value) => {
    if (assessmentType === 'progress') {
      setProgressData(prev => ({
        ...prev,
        [field]: prev[field].includes(value) 
          ? prev[field].filter(item => item !== value)
          : [...prev[field], value]
      }));
    } else {
      setIncidentData(prev => ({
        ...prev,
        [field]: prev[field].includes(value) 
          ? prev[field].filter(item => item !== value)
          : [...prev[field], value]
      }));
    }
  }, [assessmentType]);

  const validateStep = (stepIndex) => {
    const stepErrors = {};
    const currentData = assessmentType === 'progress' ? progressData : incidentData;
    
    if (stepIndex === 0) {
      if (!currentData.email || currentData.email.trim() === '') {
        stepErrors.email = t('forms.errors.emailRequired');
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(currentData.email)) {
          stepErrors.email = t('forms.errors.emailInvalid');
        }
      }
      
      if (assessmentType === 'progress') {
        if (progressData.assessmentDuration === 'custom') {
          if (!progressData.assessmentStartDate) {
            stepErrors.assessmentStartDate = t('forms.errors.startDateRequired');
          } else if (!validateDate(progressData.assessmentStartDate)) {
            stepErrors.assessmentStartDate = t('forms.errors.startDateFuture');
          }
          
          if (!progressData.assessmentEndDate) {
            stepErrors.assessmentEndDate = t('forms.errors.endDateRequired');
          } else if (!validateDate(progressData.assessmentEndDate)) {
            stepErrors.assessmentEndDate = t('forms.errors.endDateFuture');
          }
          
          if (progressData.assessmentStartDate && progressData.assessmentEndDate) {
            const startDate = new Date(progressData.assessmentStartDate);
            const endDate = new Date(progressData.assessmentEndDate);
            if (startDate > endDate) {
              stepErrors.assessmentEndDate = t('forms.errors.endDateBeforeStart');
            }
          }
        }
      }
    }
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, getCurrentSteps().length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const currentData = assessmentType === 'progress' ? progressData : incidentData;
      const submissionData = {
        ...currentData,
        assessment_type: assessmentType,
        submission_timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        language: currentLanguage
      };

      const response = await fetch(`${API_BASE_URL}/api/submit-assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }

      await response.json();
      setSubmitStatus('success');
      
      setTimeout(() => {
        if (assessmentType === 'progress') {
          const { startDate, endDate } = getDateRangeForDuration('week');
          setProgressData({
            email: '', assessmentDuration: 'week', assessmentStartDate: startDate, assessmentEndDate: endDate,
            age: '', gender: '', genderOther: '', location: '', incidentCount: '', overallStressLevel: 5,
            majorLifeEvents: '', frequentEmotions: [], otherFrequentEmotions: '', challengingSituations: [],
            otherChallengingSituation: '', timePatterns: [], otherTimePatterns: '', environmentalFactors: [],
            otherEnvironmentalFactors: '', selfAwareness: 5, selfRegulation: 5, emotionalBalance: 5, empathy: 5,
            socialSkills: 5, copingStrategies: [], otherCopingStrategies: '', copingEffectiveness: 5,
            newStrategiesTried: [], otherNewStrategies: '', biggestWin: '',
            biggestStruggle: '', mainTriggers: '', focusArea: '', satisfactionLevel: 5,
            goalsNextPeriod: '', situationsToImprove: '', supportNeeded: '', talkWithAdmin: false
          });
        } else {
          setIncidentData({
            email: '', age: '', gender: '', genderOther: '', location: '', incidentLocation: '',
            otherIncidentLocation: '', peopleInvolved: '', peopleInvolvedOther: '', incidentSeverity: 3,
            primaryEmotion: '', secondaryEmotions: [], otherSecondaryEmotions: '', emotionIntensity: 5,
            emotionDuration: '', triggerCategory: '', triggerCategoryOther: '', situationDescription: '',
            triggerFamiliarity: '', physicalSensations: [], otherPhysicalSensations: '', immediateReaction: '',
            howYouHandledIt: [], otherHowYouHandledIt: '', responseEffectiveness: 5, whatYouWishYouDid: '',
            howYouFeel: '', currentIntensity: 5, keyInsight: '',
            wantPersonalizedReport: false, recurringPattern: false,
            discussWithAdmin: false, priorityLevel: 'medium'
          });
        }
        setCurrentStep(0);
        setSubmitStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(0);
    setErrors({});
    setSubmitStatus(null);
  };

  // Get translated options
  const getGenderOptions = () => [
    { value: 'female', label: t('forms.options.genders.female') },
    { value: 'male', label: t('forms.options.genders.male') },
    { value: 'non-binary', label: t('forms.options.genders.nonBinary') },
    { value: 'transgender', label: t('forms.options.genders.transgender') },
    { value: 'genderfluid', label: t('forms.options.genders.genderfluid') },
    { value: 'agender', label: t('forms.options.genders.agender') },
    { value: 'prefer-not-to-say', label: t('forms.options.genders.preferNotToSay') }
  ];

  const getDurationOptions = () => [
    { value: 'week', label: t('forms.options.durations.week') },
    { value: 'month', label: t('forms.options.durations.month') },
    { value: '3month', label: t('forms.options.durations.threeMonth') },
    { value: 'year', label: t('forms.options.durations.year') },
    { value: 'custom', label: t('forms.options.durations.custom') }
  ];

  const getLocationOptions = () => [
    { value: 'home', label: t('forms.options.locations.home') },
    { value: 'work', label: t('forms.options.locations.work') },
    { value: 'school', label: t('forms.options.locations.school') },
    { value: 'public', label: t('forms.options.locations.public') },
    { value: 'transportation', label: t('forms.options.locations.transportation') },
    { value: 'online', label: t('forms.options.locations.online') },
    { value: 'social-event', label: t('forms.options.locations.socialEvent') },
    { value: 'healthcare', label: t('forms.options.locations.healthcare') },
    { value: 'shopping', label: t('forms.options.locations.shopping') },
    { value: 'outdoors', label: t('forms.options.locations.outdoors') }
  ];

  const getPeopleInvolvedOptions = () => [
    { value: 'alone', label: t('forms.options.peopleInvolved.alone') },
    { value: 'partner', label: t('forms.options.peopleInvolved.partner') },
    { value: 'family', label: t('forms.options.peopleInvolved.family') },
    { value: 'friends', label: t('forms.options.peopleInvolved.friends') },
    { value: 'colleagues', label: t('forms.options.peopleInvolved.colleagues') },
    { value: 'classmates', label: t('forms.options.peopleInvolved.classmates') },
    { value: 'strangers', label: t('forms.options.peopleInvolved.strangers') },
    { value: 'authority-figures', label: t('forms.options.peopleInvolved.authority') },
    { value: 'service-workers', label: t('forms.options.peopleInvolved.service') }
  ];

  const getEmotionDurationOptions = () => [
    { value: 'seconds', label: t('forms.options.emotionDurations.seconds') },
    { value: 'minutes', label: t('forms.options.emotionDurations.minutes') },
    { value: '15-30min', label: t('forms.options.emotionDurations.15-30min') },
    { value: '1-2hours', label: t('forms.options.emotionDurations.1-2hours') },
    { value: 'several-hours', label: t('forms.options.emotionDurations.severalHours') },
    { value: 'all-day', label: t('forms.options.emotionDurations.allDay') },
    { value: 'multiple-days', label: t('forms.options.emotionDurations.multipleDays') },
    { value: 'week-or-more', label: t('forms.options.emotionDurations.weekOrMore') }
  ];

  const getTriggerFamiliarityOptions = () => [
    { value: 'very-familiar', label: t('forms.options.triggerFamiliarity.veryFamiliar') },
    { value: 'somewhat-familiar', label: t('forms.options.triggerFamiliarity.somewhatFamiliar') },
    { value: 'mixed', label: t('forms.options.triggerFamiliarity.mixed') },
    { value: 'somewhat-unexpected', label: t('forms.options.triggerFamiliarity.somewhatUnexpected') },
    { value: 'completely-unexpected', label: t('forms.options.triggerFamiliarity.completelyUnexpected') }
  ];

  const getHandlingFeelingsOptions = () => [
    { value: 'proud', label: t('forms.options.handlingFeelings.proud') },
    { value: 'satisfied', label: t('forms.options.handlingFeelings.satisfied') },
    { value: 'neutral', label: t('forms.options.handlingFeelings.neutral') },
    { value: 'disappointed', label: t('forms.options.handlingFeelings.disappointed') },
    { value: 'regretful', label: t('forms.options.handlingFeelings.regretful') }
  ];

  const getPriorityLevelOptions = () => [
    { value: 'low', label: t('forms.options.priorityLevels.low') },
    { value: 'medium', label: t('forms.options.priorityLevels.medium') },
    { value: 'high', label: t('forms.options.priorityLevels.high') },
    { value: 'urgent', label: t('forms.options.priorityLevels.urgent') }
  ];

  // Step Content Renderers
  const renderProgressStepContent = () => {
    const currentData = progressData;
    
    switch (currentStep) {
      case 0: 
        return (
          <div className="step-content">
            <div className="mindful-prompt">
              <Lightbulb size={20} />
              <div>
                <strong>{t('prompts.progressGrounding.title')}</strong><br />
                {t('prompts.progressGrounding.content')}
              </div>
            </div>

            <div className="compact-form-grid">
              <InputCard
                field="email"
                label={t('forms.labels.email')}
                type="email"
                placeholder={t('forms.labels.emailPlaceholder')}
                icon="📧"
                value={currentData.email}
                onChange={handleInputChange}
                error={errors.email}
              />
              
              <SelectCard
                field="assessmentDuration"
                label={t('forms.labels.assessmentDuration')}
                icon="📅"
                value={currentData.assessmentDuration}
                onChange={handleInputChange}
                onDurationChange={handleDurationChange}
                options={getDurationOptions()}
                t={t}
              />

              <InputCard
                field="age"
                label={t('forms.labels.age')}
                type="number"
                placeholder={t('forms.labels.agePlaceholder')}
                icon="🎂"
                value={currentData.age}
                onChange={handleInputChange}
                error={errors.age}
              />

              <SelectCard
                field="gender"
                label={t('forms.labels.genderIdentity')}
                icon="⚥"
                value={currentData.gender}
                otherValue={currentData.genderOther}
                onChange={handleInputChange}
                options={getGenderOptions()}
                showOther={true}
                otherField="genderOther"
                t={t}
              />

              <InputCard
                field="location"
                label={t('forms.labels.location')}
                placeholder={t('forms.labels.locationPlaceholder')}
                icon="🌍"
                value={currentData.location}
                onChange={handleInputChange}
                error={errors.location}
              />

              <InputCard
                field="incidentCount"
                label={t('forms.labels.incidentCount')}
                type="number"
                placeholder={t('forms.labels.incidentCountPlaceholder')}
                icon="📊"
                value={currentData.incidentCount}
                onChange={handleInputChange}
                error={errors.incidentCount}
              />
            </div>

            {/* Date Range Section - Only show for custom */}
            {progressData.assessmentDuration === 'custom' && (
              <div className="date-range-section">
                <h3 className="category-title">
                  📅 {t('sections.customDateRange')}
                </h3>
                <div className="date-range-grid">
                  <InputCard
                    field="assessmentStartDate"
                    label={t('forms.labels.startDate')}
                    type="date"
                    icon="📅"
                    max={getMaxDate()}
                    value={currentData.assessmentStartDate}
                    onChange={handleInputChange}
                    error={errors.assessmentStartDate}
                  />
                  <InputCard
                    field="assessmentEndDate"
                    label={t('forms.labels.endDate')}
                    type="date"
                    icon="📅"
                    min={progressData.assessmentStartDate || null}
                    max={getMaxDate()}
                    value={currentData.assessmentEndDate}
                    onChange={handleInputChange}
                    error={errors.assessmentEndDate}
                  />
                </div>
                <div className="date-helper-text">
                  💡 {t('sections.dateHelperText')}
                </div>
              </div>
            )}

            {/* Current Assessment Period Display */}
            {progressData.assessmentDuration && progressData.assessmentDuration !== 'custom' && (
              <div className="current-period-display">
                <div className="period-info">
                  📅 <strong>{t('sections.assessmentPeriod')}:</strong> {progressData.assessmentStartDate} to {progressData.assessmentEndDate}
                </div>
              </div>
            )}

            <div className="single-column-section">
              <SliderCard
                field="overallStressLevel"
                label={t('forms.labels.overallStressLevel')}
                min={1}
                max={10}
                icon="📈"
                value={currentData.overallStressLevel}
                onChange={handleInputChange}
                t={t}
              />
            </div>

            <TextAreaCard
              field="majorLifeEvents"
              label={t('forms.labels.majorLifeEvents')}
              placeholder={t('forms.labels.majorLifeEventsPlaceholder')}
              icon="🌟"
              value={currentData.majorLifeEvents}
              onChange={handleInputChange}
            />
          </div>
        );

      case 1:
        return (
          <div className="step-content">
            <EmotionSelector
              field="frequentEmotions"
              label={t('sections.selectEmotions')}
              categories={allEmotions}
              icon="❤️"
              value={currentData.frequentEmotions}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherFrequentEmotions"
              otherValue={currentData.otherFrequentEmotions}
              t={t}
            />
            
            <EmotionSelector
              field="challengingSituations"
              label={t('sections.challengingSituations')}
              options={challengingSituations}
              icon="⚠️"
              value={currentData.challengingSituations}
              otherValue={currentData.otherChallengingSituation}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherChallengingSituation"
              t={t}
            />

            <EmotionSelector
              field="timePatterns"
              label={t('sections.timePatterns')}
              options={detailedTimePatterns}
              icon="⏰"
              value={currentData.timePatterns}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherTimePatterns"
              otherValue={currentData.otherTimePatterns}
              t={t}
            />

            <EmotionSelector
              field="environmentalFactors"
              label={t('sections.environmentalFactors')}
              options={detailedEnvironmentalFactors}
              icon="🌍"
              value={currentData.environmentalFactors}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherEnvironmentalFactors"
              otherValue={currentData.otherEnvironmentalFactors}
              t={t}
            />
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="mindful-prompt">
              <Brain size={20} />
              <div>
                <strong>{t('prompts.honestAssessment.title')}</strong><br />
                {t('prompts.honestAssessment.content')}
              </div>
            </div>

            <div className="form-grid">
              <SliderCard
                field="selfAwareness"
                label={t('forms.labels.selfAwareness')}
                min={1}
                max={10}
                icon="🎯"
                value={currentData.selfAwareness}
                onChange={handleInputChange}
                t={t}
              />
              <SliderCard
                field="selfRegulation"
                label={t('forms.labels.selfRegulation')}
                min={1}
                max={10}
                icon="⚖️"
                value={currentData.selfRegulation}
                onChange={handleInputChange}
                t={t}
              />
              <SliderCard
                field="emotionalBalance"
                label={t('forms.labels.emotionalBalance')}
                min={1}
                max={10}
                icon="🧘‍♀️"
                value={currentData.emotionalBalance}
                onChange={handleInputChange}
                t={t}
              />
              <SliderCard
                field="empathy"
                label={t('forms.labels.empathy')}
                min={1}
                max={10}
                icon="🤝"
                value={currentData.empathy}
                onChange={handleInputChange}
                t={t}
              />
              <SliderCard
                field="socialSkills"
                label={t('forms.labels.socialSkills')}
                min={1}
                max={10}
                icon="👥"
                value={currentData.socialSkills}
                onChange={handleInputChange}
                t={t}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <EmotionSelector
              field="copingStrategies"
              label={t('sections.copingStrategies')}
              options={copingStrategies}
              icon="🛡️"
              value={currentData.copingStrategies}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherCopingStrategies"
              otherValue={currentData.otherCopingStrategies}
              t={t}
            />
            
            <div className="single-column-section">
              <SliderCard
                field="copingEffectiveness"
                label={t('forms.labels.copingEffectiveness')}
                min={1}
                max={10}
                icon="📊"
                value={currentData.copingEffectiveness}
                onChange={handleInputChange}
                t={t}
              />
            </div>

            <EmotionSelector
              field="newStrategiesTried"
              label={t('sections.newStrategies')}
              options={newCopingStrategiesOptions}
              icon="🆕"
              value={currentData.newStrategiesTried}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherNewStrategies"
              otherValue={currentData.otherNewStrategies}
              t={t}
            />
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <div className="form-grid">
              <TextAreaCard
                field="biggestWin"
                label={t('forms.labels.biggestWin')}
                placeholder={t('forms.labels.biggestWinPlaceholder')}
                icon="🏆"
                value={currentData.biggestWin}
                onChange={handleInputChange}
              />

              <TextAreaCard
                field="biggestStruggle"
                label={t('forms.labels.biggestStruggle')}
                placeholder={t('forms.labels.biggestStrugglePlaceholder')}
                icon="💪"
                value={currentData.biggestStruggle}
                onChange={handleInputChange}
              />

              <TextAreaCard
                field="mainTriggers"
                label={t('forms.labels.mainTriggers')}
                placeholder={t('forms.labels.mainTriggersPlaceholder')}
                icon="⚡"
                value={currentData.mainTriggers}
                onChange={handleInputChange}
              />

              <TextAreaCard
                field="situationsToImprove"
                label={t('forms.labels.situationsToImprove')}
                placeholder={t('forms.labels.situationsToImprovePlaceholder')}
                icon="🎯"
                value={currentData.situationsToImprove}
                onChange={handleInputChange}
              />
            </div>

            <div className="single-column-section">
              <SliderCard
                field="satisfactionLevel"
                label={t('forms.labels.satisfactionLevel')}
                min={1}
                max={10}
                icon="😊"
                value={currentData.satisfactionLevel}
                onChange={handleInputChange}
                t={t}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <div className="form-grid">
              <TextAreaCard
                field="focusArea"
                label={t('forms.labels.focusArea')}
                placeholder={t('forms.labels.focusAreaPlaceholder')}
                icon="🎯"
                value={currentData.focusArea}
                onChange={handleInputChange}
              />

              <TextAreaCard
                field="goalsNextPeriod"
                label={t('forms.labels.goalsNextPeriod')}
                placeholder={t('forms.labels.goalsNextPeriodPlaceholder')}
                icon="🌟"
                value={currentData.goalsNextPeriod}
                onChange={handleInputChange}
              />
            </div>

            <div className="support-section">
              <h3 className="category-title">
                🤝 {t('sections.supportResources')}
              </h3>
              
              <TextAreaCard
                field="supportNeeded"
                label={t('forms.labels.supportNeeded')}
                placeholder={t('forms.labels.supportNeededPlaceholder')}
                icon="🤝"
                value={currentData.supportNeeded}
                onChange={handleInputChange}
              />

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">📞</span>
                  {t('forms.labels.talkWithAdmin')}
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={progressData.talkWithAdmin || false}
                    onChange={(e) => handleInputChange('talkWithAdmin', e.target.checked)}
                    className="checkbox"
                  />
                  <span>{t('forms.labels.talkWithAdminYes')}</span>
                </label>
                
                {progressData.talkWithAdmin && (
                  <div className="admin-contact-info">
                    <div className="contact-card">
                      <div className="contact-header">
                        <span className="contact-icon">👨‍💼</span>
                        <h4>{t('sections.adminContact.title')}</h4>
                      </div>
                      <div className="contact-details">
                        <p><strong>{t('sections.adminContact.phone')}:</strong> <a href="tel:+919427826685">+91 94278 26685</a></p>
                        <p><em>{t('sections.adminContact.message')}</em></p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderIncidentStepContent = () => {
    const currentData = incidentData;
    
    switch (currentStep) {
      case 0:
        return (
          <div className="step-content">
            <div className="mindful-prompt">
              <Lightbulb size={20} />
              <div>
                <strong>{t('prompts.incidentGrounding.title')}</strong><br />
                {t('prompts.incidentGrounding.content')}
              </div>
            </div>

            <div className="compact-form-grid">
              <InputCard
                field="email"
                label={t('forms.labels.email')}
                type="email"
                placeholder={t('forms.labels.emailPlaceholder')}
                icon="📧"
                value={currentData.email}
                onChange={handleInputChange}
                error={errors.email}
              />
              
              <InputCard
                field="age"
                label={t('forms.labels.age')}
                type="number"
                placeholder={t('forms.labels.agePlaceholder')}
                icon="🎂"
                value={currentData.age}
                onChange={handleInputChange}
                error={errors.age}
              />

              <SelectCard
                field="gender"
                label={t('forms.labels.genderIdentity')}
                icon="⚥"
                value={currentData.gender}
                otherValue={currentData.genderOther}
                onChange={handleInputChange}
                options={getGenderOptions()}
                showOther={true}
                otherField="genderOther"
                t={t}
              />

              <InputCard
                field="location"
                label={t('forms.labels.location')}
                placeholder={t('forms.labels.locationPlaceholder')}
                icon="🌍"
                value={currentData.location}
                onChange={handleInputChange}
                error={errors.location}
              />

              <SelectCard
                field="incidentLocation"
                label={t('forms.labels.whereHappened')}
                icon="📍"
                value={currentData.incidentLocation}
                otherValue={currentData.otherIncidentLocation}
                onChange={handleInputChange}
                options={getLocationOptions()}
                showOther={true}
                otherField="otherIncidentLocation"
                t={t}
              />

              <SelectCard
                field="peopleInvolved"
                label={t('forms.labels.whoInvolved')}
                icon="👥"
                value={currentData.peopleInvolved}
                otherValue={currentData.peopleInvolvedOther}
                onChange={handleInputChange}
                options={getPeopleInvolvedOptions()}
                showOther={true}
                otherField="peopleInvolvedOther"
                t={t}
              />
            </div>

            <div className="single-column-section">
              <SliderCard
                field="incidentSeverity"
                label={t('forms.labels.incidentSeverity')}
                min={1}
                max={10}
                icon="📊"
                value={currentData.incidentSeverity}
                onChange={handleInputChange}
                t={t}
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="step-content">
            <div className="form-grid">
              <SelectCard
                field="primaryEmotion"
                label={t('forms.labels.primaryEmotion')}
                icon="❤️"
                value={currentData.primaryEmotion}
                onChange={handleInputChange}
                options={[
                  ...allEmotions.positive.map(e => ({ value: e.value, label: e.label })),
                  ...allEmotions.neutral.map(e => ({ value: e.value, label: e.label })),
                  ...allEmotions.challenging.map(e => ({ value: e.value, label: e.label })),
                  ...allEmotions.physical.map(e => ({ value: e.value, label: e.label })),
                  ...allEmotions.complex.map(e => ({ value: e.value, label: e.label }))
                ]}
                t={t}
              />

              <SliderCard
                field="emotionIntensity"
                label={t('forms.labels.emotionIntensity')}
                min={1}
                max={10}
                icon="📊"
                value={currentData.emotionIntensity}
                onChange={handleInputChange}
                t={t}
              />

              <SelectCard
                field="emotionDuration"
                label={t('forms.labels.emotionDuration')}
                icon="⏰"
                value={currentData.emotionDuration}
                onChange={handleInputChange}
                options={getEmotionDurationOptions()}
                t={t}
              />
            </div>

            <EmotionSelector
              field="secondaryEmotions"
              label={t('sections.secondaryEmotions')}
              categories={allEmotions}
              icon="💭"
              value={currentData.secondaryEmotions}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherSecondaryEmotions"
              otherValue={currentData.otherSecondaryEmotions}
              t={t}
            />

            <EmotionSelector
              field="physicalSensations"
              label={t('sections.physicalSensations')}
              options={[
                { value: 'head-tension', label: '🧠 Head tension/pressure' },
                { value: 'jaw-clenching', label: '😬 Jaw clenching' },
                { value: 'throat-tightness', label: '😶 Throat tightness' },
                { value: 'chest-tightness', label: '🫁 Chest tightness' },
                { value: 'heart-racing', label: '💓 Heart racing' },
                { value: 'stomach-knots', label: '🤢 Stomach knots/butterflies' },
                { value: 'muscle-tension', label: '💪 Muscle tension' },
                { value: 'shaking-trembling', label: '🫨 Shaking/trembling' },
                { value: 'fatigue-heaviness', label: '😴 Fatigue/heaviness' },
                { value: 'restlessness', label: '🏃‍♂️ Restlessness/agitation' },
                { value: 'sweating', label: '💦 Sweating' },
                { value: 'cold-chills', label: '🥶 Cold chills' },
                { value: 'nausea', label: '🤢 Nausea' },
                { value: 'dizziness', label: '😵‍💫 Dizziness' }
              ]}
              icon="🎯"
              value={currentData.physicalSensations}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherPhysicalSensations"
              otherValue={currentData.otherPhysicalSensations}
              t={t}
            />
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="form-grid">
              <SelectCard
                field="triggerCategory"
                label={t('forms.labels.triggerType')}
                icon="⚡"
                value={currentData.triggerCategory}
                otherValue={currentData.triggerCategoryOther}
                onChange={handleInputChange}
                options={triggers}
                showOther={true}
                otherField="triggerCategoryOther"
                t={t}
              />

              <SelectCard
                field="triggerFamiliarity"
                label={t('forms.labels.triggerFamiliarity')}
                icon="🤔"
                value={currentData.triggerFamiliarity}
                onChange={handleInputChange}
                options={getTriggerFamiliarityOptions()}
                t={t}
              />
            </div>

            <TextAreaCard
              field="situationDescription"
              label={t('forms.labels.situationDescription')}
              placeholder={t('forms.labels.situationDescriptionPlaceholder')}
              icon="📖"
              value={currentData.situationDescription}
              onChange={handleInputChange}
            />
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <TextAreaCard
              field="immediateReaction"
              label={t('forms.labels.immediateReaction')}
              placeholder={t('forms.labels.immediateReactionPlaceholder')}
              icon="⚡"
              value={currentData.immediateReaction}
              onChange={handleInputChange}
            />

            <EmotionSelector
              field="howYouHandledIt"
              label={t('sections.howHandled')}
              options={copingStrategies}
              icon="🛡️"
              value={currentData.howYouHandledIt}
              onMultiSelect={handleMultiSelect}
              onChange={handleInputChange}
              showOther={true}
              otherField="otherHowYouHandledIt"
              otherValue={currentData.otherHowYouHandledIt}
              t={t}
            />

            <div className="single-column-section">
              <SliderCard
                field="responseEffectiveness"
                label={t('forms.labels.responseEffectiveness')}
                min={1}
                max={10}
                icon="📊"
                value={currentData.responseEffectiveness}
                onChange={handleInputChange}
                t={t}
              />
            </div>

            <TextAreaCard
              field="whatYouWishYouDid"
              label={t('forms.labels.wishDifferently')}
              placeholder={t('forms.labels.wishDifferentlyPlaceholder')}
              icon="💭"
              value={currentData.whatYouWishYouDid}
              onChange={handleInputChange}
            />
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <div className="form-grid">
              <SelectCard
                field="howYouFeel"
                label={t('forms.labels.howYouFeelAboutHandling')}
                icon="🤔"
                value={currentData.howYouFeel}
                onChange={handleInputChange}
                options={getHandlingFeelingsOptions()}
                t={t}
              />

              <SliderCard
                field="currentIntensity"
                label={t('forms.labels.currentIntensity')}
                min={1}
                max={10}
                icon="📊"
                value={currentData.currentIntensity}
                onChange={handleInputChange}
                t={t}
              />
            </div>

            <TextAreaCard
              field="keyInsight"
              label={t('forms.labels.keyInsight')}
              placeholder={t('forms.labels.keyInsightPlaceholder')}
              icon="💡"
              value={currentData.keyInsight}
              onChange={handleInputChange}
            />
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">📊</span>
                  {t('forms.labels.personalizedReport')}
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={incidentData.wantPersonalizedReport || false}
                    onChange={(e) => handleInputChange('wantPersonalizedReport', e.target.checked)}
                    className="checkbox"
                  />
                  <span>{t('forms.labels.personalizedReportYes')}</span>
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">🔄</span>
                  {t('forms.labels.recurringPattern')}
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={incidentData.recurringPattern || false}
                    onChange={(e) => handleInputChange('recurringPattern', e.target.checked)}
                    className="checkbox"
                  />
                  <span>{t('forms.labels.recurringPatternYes')}</span>
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">👥</span>
                  {t('forms.labels.discussWithAdmin')}
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={incidentData.discussWithAdmin || false}
                    onChange={(e) => handleInputChange('discussWithAdmin', e.target.checked)}
                    className="checkbox"
                  />
                  <span>{t('forms.labels.discussWithAdminYes')}</span>
                </label>
                
                {incidentData.discussWithAdmin && (
                  <div className="admin-contact-info">
                    <div className="contact-card">
                      <div className="contact-header">
                        <span className="contact-icon">👨‍💼</span>
                        <h4>{t('sections.adminContact.title')}</h4>
                      </div>
                      <div className="contact-details">
                        <p><strong>{t('sections.adminContact.phone')}:</strong> <a href="tel:+919427826685">+91 94278 26685</a></p>
                        <p><em>{t('sections.adminContact.message')}</em></p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <SelectCard
                field="priorityLevel"
                label={t('forms.labels.priorityLevel')}
                icon="🎯"
                value={currentData.priorityLevel}
                onChange={handleInputChange}
                options={getPriorityLevelOptions()}
                t={t}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container">
      {/* Header - Updated with side-by-side layout */}
      <div className="header">
        <div className="header-top">
          <div className="header-content">
            <h1 className="main-title">{t('app.title')}</h1>
            <p className="subtitle">{t('app.subtitle')}</p>
          </div>
          <LanguageSelector />
        </div>
        
        {/* Side-by-side notes layout */}
        <div className="notes-container">
          <div className="privacy-note">
            🔐 <strong>{t('app.privacyNote.title')}</strong><br />
            <strong>{t('app.privacyNote.content')}</strong>
          </div>
          <div className="honesty-note">
            ❤️ <strong>{t('app.honestyNote.title')}</strong><br />
            <strong>{t('app.honestyNote.content')}</strong>
          </div>
        </div>
      </div>

      {/* Toggle Container */}
      <div className="toggle-container">
        <div className="toggle">
          <div 
            className={`toggle-option ${assessmentType === 'progress' ? 'toggle-option-active' : 'toggle-option-inactive'}`}
            onClick={() => {
              setAssessmentType('progress');
              resetForm();
            }}
          >
            <Calendar size={20} />
            {t('navigation.progressTracker')}
          </div>
          <div 
            className={`toggle-option ${assessmentType === 'incident' ? 'toggle-option-active' : 'toggle-option-inactive'}`}
            onClick={() => {
              setAssessmentType('incident');
              resetForm();
            }}
          >
            <Zap size={20} />
            {t('navigation.incidentLogger')}
          </div>
        </div>
      </div>

      {/* Progress Container */}
      {assessmentType && (
        <div className="progress-container">
          <div className="progress-header">
            <h2 className="progress-title">
              {assessmentType === 'progress' ? t('progressTitle') : t('incidentTitle')}
            </h2>
            <div className="progress-stats">
              {t('messages.step')} {currentStep + 1} {t('messages.of')} {getCurrentSteps().length}
            </div>
          </div>
          
          <div className="progress-steps">
            {getCurrentSteps().map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div
                  key={index}
                  className={`progress-step ${isActive ? 'progress-step-active' : ''} ${isCompleted ? 'progress-step-completed' : ''}`}
                >
                  <div className="step-icon">
                    <Icon size={18} />
                  </div>
                  <div>
                    <div style={{fontWeight: '700', marginBottom: '0.2rem', fontSize: '0.9rem'}}>
                      {step.title}
                    </div>
                    <div style={{fontSize: '0.8rem', opacity: 0.8}}>
                      {step.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Form Container */}
      <div className="form-container">
        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div className="message message-success">
            <CheckCircle size={24} />
            <div>
              <h3 style={{margin: 0, marginBottom: '0.5rem'}}>{t('messages.submissionSuccess.title')}</h3>
              <p style={{margin: 0}}>{t('messages.submissionSuccess.content')}</p>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="message message-error">
            <AlertCircle size={24} />
            <div>
              <h3 style={{margin: 0, marginBottom: '0.5rem'}}>{t('messages.submissionError.title')}</h3>
              <p style={{margin: 0}}>{t('messages.submissionError.content')}</p>
            </div>
          </div>
        )}

        {/* Step Header */}
        {assessmentType && (
          <div className="step-header">
            <div className="step-icon">
              {React.createElement(getCurrentSteps()[currentStep]?.icon || Brain, { size: 24 })}
            </div>
            <div>
              <h2 className="step-title">{getCurrentSteps()[currentStep]?.title}</h2>
              <p className="step-description">{getCurrentSteps()[currentStep]?.description}</p>
            </div>
          </div>
        )}

        {/* Step Content */}
        {assessmentType === 'progress' && renderProgressStepContent()}
        {assessmentType === 'incident' && renderIncidentStepContent()}
      </div>

      {/* Navigation */}
      {assessmentType && (
        <div className="navigation">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`nav-btn nav-btn-secondary ${currentStep === 0 ? 'disabled' : ''}`}
          >
            <ChevronLeft size={16} />
            {t('navigation.previousStep')}
          </button>

          <div style={{color: '#4a5568', fontSize: '0.9rem', fontWeight: '500', textAlign: 'center'}}>
            {assessmentType === 'progress' ? t('navigation.deepReflection') : t('navigation.emotionalProcessing')}
          </div>

          {currentStep === getCurrentSteps().length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`nav-btn submit-btn ${isSubmitting ? 'disabled' : ''}`}
            >
              {isSubmitting ? (
                t('navigation.submitting')
              ) : (
                <>
                  <Send size={16} />
                  {t('navigation.completeAssessment')}
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextStep}
              className="nav-btn nav-btn-primary"
            >
              {t('navigation.continueJourney')}
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmotionalIntelligenceAssessmentPlatform;